import { useState } from 'react'
import styled from 'styled-components'

import { COLORS } from '../../commons/Colors'

export const AVAILABLE_ROLES = [
    { id: 'REPRESENTANTE', name: 'Representante' },
    { id: 'SUPERVISOR', name: 'Supervisor' },
    { id: 'ACCOUNT MANAGER', name: 'Account Manager' },
    { id: 'GERENTE DISTRITAL', name: 'Gerente Distrital' },
    { id: 'ATC', name: 'ATC' },
]

type Props = {
    label: string
    helperText?: string
    selectedRoles: string[]
    onRoleAdd: (roleId: string) => void
    onRoleRemove: (roleId: string) => void
}

export default function RoleSelector({
    label,
    helperText,
    selectedRoles,
    onRoleAdd,
    onRoleRemove,
}: Props) {
    const [isOpen, setIsOpen] = useState(false)
    const selectedSet = new Set(selectedRoles)
    const availableRoles = AVAILABLE_ROLES.filter(
        (role) => !selectedSet.has(role.id)
    )

    const handleRemoveChip = (roleId: string) => {
        onRoleRemove(roleId)
    }

    const handleSelectRole = (roleId: string) => {
        onRoleAdd(roleId)
        setIsOpen(false)
    }

    return (
        <Container>
            <Label>{label}</Label>

            <SelectWrapper>
                <ChipsContainer>
                    {selectedRoles.map((roleId) => {
                        const role = AVAILABLE_ROLES.find(
                            (r) => r.id === roleId
                        )
                        return (
                            <Chip key={roleId}>
                                <ChipText>{role?.name || roleId}</ChipText>
                                <RemoveButton
                                    type="button"
                                    onClick={() => handleRemoveChip(roleId)}
                                >
                                    ×
                                </RemoveButton>
                            </Chip>
                        )
                    })}
                </ChipsContainer>

                {availableRoles.length > 0 && (
                    <SelectButton
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        + Adicionar role
                    </SelectButton>
                )}
            </SelectWrapper>

            {isOpen && availableRoles.length > 0 && (
                <OptionsContainer>
                    {availableRoles.map((role) => (
                        <OptionItem
                            key={role.id}
                            onClick={() => handleSelectRole(role.id)}
                        >
                            {role.name}
                        </OptionItem>
                    ))}
                </OptionsContainer>
            )}

            {helperText && <HelperText>{helperText}</HelperText>}
        </Container>
    )
}

const Container = styled.div`
    position: relative;
`

const Label = styled.label`
    display: block;
    font-size: 13px;
    font-weight: bold;
    color: ${COLORS.primaryGray};
    margin-bottom: 8px;
`

const SelectWrapper = styled.div`
    border: 1px solid ${COLORS.borderGray};
    border-radius: 4px;
    padding: 8px;
    min-height: 40px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    background: white;
`

const ChipsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`

const Chip = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f3e5f5;
    border: 1px solid #ce93d8;
    border-radius: 20px;
    padding: 4px 12px;
    font-size: 12px;
    color: #7b1fa2;
`

const ChipText = styled.span`
    word-break: break-word;
    max-width: 200px;
`

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: #7b1fa2;
    cursor: pointer;
    font-size: 18px;
    padding: 0;
    line-height: 1;
    display: flex;
    align-items: center;

    &:hover {
        opacity: 0.7;
    }
`

const SelectButton = styled.button`
    background: none;
    border: none;
    color: #7b1fa2;
    cursor: pointer;
    font-size: 12px;
    padding: 0;
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }
`

const OptionsContainer = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid ${COLORS.borderGray};
    border-top: none;
    border-radius: 0 0 4px 4px;
    z-index: 10;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`

const OptionItem = styled.div`
    padding: 8px 12px;
    cursor: pointer;
    font-size: 13px;
    color: ${COLORS.primaryGray};
    background: white;

    &:hover {
        background: #f5f5f5;
    }
`

const HelperText = styled.span`
    display: block;
    font-size: 11px;
    color: #999;
    margin-top: 4px;
    font-weight: normal;
`
